import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

/* INGEN framer-motion her. Modulet indlæses af hver offentlig side, og framer
   skriver `opacity: 0` som inline style, så prerenderingen snapshottede teksten
   skjult. Samtidig lå biblioteket i hovedchunken sammen med forsiden. Alt herunder
   er CSS-animationer, IntersectionObserver og rene transform-opdateringer. */

/* -------------------- Brand tokens (BLUE DNA → futuristic void) -------------------- */
export const PRIMARY = "hsl(210, 100%, 45%)";
export const PRIMARY_DARK = "hsl(210, 100%, 35%)";
export const EBLUE = "#0055FF";
export const CYAN = "#00E5FF";
export const INK = "#05060E"; // deep void for dark sections
export const INK_SOFT = "#0A0B14";

/* -------------------- Motion helpers -------------------- */
/* Ind- og udtoning laves med CSS-klassen `.votra-rise` i index.css, hvor `opacity`
   kun står i @keyframes. Indholdet er dermed synligt, hvis animationen ikke kører
   (prerender, bots, reduceret motion). */
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const Reveal = ({ children, i = 0, className = "", as: As = "div", style, ...rest }) => (
  <As
    className={`votra-rise ${className}`}
    style={i ? { animationDelay: `${Math.min(i, 5) * 80}ms`, ...style } : style}
    {...rest}
  >
    {children}
  </As>
);

/* Et kort skal være et rigtigt <a href>: Google kan ikke følge et onClick. */
export const MotionLink = Link;

/* Uppercase tracked mono label (AI-native tech accent) */
export const Overline = ({ children, light, className = "" }) => (
  <p
    className={`font-tech text-[11px] font-medium uppercase tracking-[0.28em] mb-4 ${className}`}
    style={{ color: light ? "#2E6BFF" : PRIMARY }}
  >
    {children}
  </p>
);

/* Rich futuristic aurora/mesh background for dark (void) sections */
export const AuroraLight = () => (
  <div className="kursus-aurora" aria-hidden>
    <div className="fx-aurora-light"><div className="lb lb--blue" /><div className="lb lb--violet" /><div className="lb lb--cyan" /></div>
    <div className="fx-mesh-light" />
  </div>
);

/* Rich futuristic aurora/mesh background for dark (void) sections */
export const AuroraBlue = ({ grid = true, nodes = true }) => (
  <div className="kursus-aurora" aria-hidden>
    {nodes && <div className="fx-nodes" />}
    {grid && <div className="fx-grid" />}
    <div className="fx-blob fx-blob--eblue" />
    <div className="fx-blob fx-blob--cyan" />
    <div className="fx-blob fx-blob--violet" />
  </div>
);

/* Mouse-spotlight wrapper, sætter CSS-variablerne --mx/--my til .fx-spot / .fx-spot-border.
   `as` må både være et tagnavn ("div") og en komponent (fx Link). */
export const Spotlight = ({ children, className = "", as: As = "div", onClick, dataTestid, ...rest }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <As ref={ref} onMouseMove={onMove} onClick={onClick} data-testid={dataTestid} className={className} {...rest}>
      {children}
    </As>
  );
};

/* Magnetic CTA med mus-følgende forskydning. Får den `to` eller `href`, renderes den
   som et rigtigt anker, så Google kan følge linket. Uden dem er det en knap. */
export const MagneticButton = ({ children, className = "", style, onClick, to, href, ...rest }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;
    const r = el.getBoundingClientRect();
    const dx = (e.clientX - (r.left + r.width / 2)) * 0.3;
    const dy = (e.clientY - (r.top + r.height / 2)) * 0.5;
    el.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
  };
  const reset = () => {
    if (ref.current) ref.current.style.transform = "translate3d(0, 0, 0)";
  };
  const shared = {
    ref,
    onMouseMove: onMove,
    onMouseLeave: reset,
    className: `votra-magnetic ${className}`,
    style,
    ...rest,
  };
  if (to) return <Link to={to} onClick={onClick} {...shared}>{children}</Link>;
  if (href) return <a href={href} onClick={onClick} {...shared}>{children}</a>;
  return <button type="button" onClick={onClick} {...shared}>{children}</button>;
};

/* Tælleanimation på statistik. Starttilstanden er den færdige værdi, så crawlere og
   AI-modeller læser "10+", ikke "0". */
export const Counter = ({ value }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(() => String(value));
  useEffect(() => {
    const el = ref.current;
    const match = String(value).match(/^(\D*)([\d.,]+)(.*)$/);
    if (!el || !match || prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      setDisplay(String(value));
      return undefined;
    }
    const prefix = match[1] || "";
    const target = parseFloat(match[2].replace(/,/g, "."));
    const suffix = match[3] || "";
    let raf = 0;
    const run = () => {
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min((now - t0) / 1600, 1);
        const eased = 1 - (1 - p) ** 3;
        setDisplay(`${prefix}${Math.round(target * eased)}${suffix}`);
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          io.disconnect();
          run();
        }
      },
      { rootMargin: "-60px" }
    );
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [value]);
  return <span ref={ref}>{display}</span>;
};

/* Section heading block: overline + serif title + optional subtitle */
export const SectionHeading = ({ label, title, subtitle, light, center, className = "" }) => (
  <div className={`${center ? "text-center max-w-2xl mx-auto" : "max-w-2xl"} ${className}`}>
    {label && (
      <div className={center ? "flex justify-center" : ""}>
        <Overline light={light}>{label}</Overline>
      </div>
    )}
    <h2
      className={`font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-tight ${light ? "text-white" : "text-slate-900"}`}
    >
      {title}
    </h2>
    {subtitle && (
      <p className={`mt-5 text-lg leading-relaxed ${light ? "text-slate-300" : "text-slate-600"}`}>
        {subtitle}
      </p>
    )}
  </div>
);
